import { XInput } from '@playcastdotio/CoreTypes';
import clsx from 'clsx';
import { HintHandIcon, useHintGlowStyles } from './HintHandIcon';

interface CircularButtonProps {
  prefix: string;
  gamepad: XInput;
  isPressed: boolean;
  iconUrl?: string;
  size?: number;
  label?: string;
  buttonType: 'Trigger' | 'Bumper' | 'North' | 'South' | 'East' | 'West' | 'StickClick' | 'Meta';
  /** Whether this specific button is currently highlighted in the attract pattern */
  attractHighlight?: boolean;
  /** Glow intensity when highlighted (0-100) */
  attractOpacity?: number;
}

/**
 * Circular button component for custom gamepad themes
 * Renders a plain gray circle with optional custom icon overlay
 * IMPORTANT: Uses same IDs as ButtonTrigger/ButtonBumper for touch input compatibility
 */
export const CircularButton = ({
  isPressed,
  iconUrl,
  size = 80,
  label,
  prefix,
  buttonType,
  attractHighlight,
}: CircularButtonProps) => {
  const buttonId = buttonType === 'StickClick'
    ? prefix.concat('StickClick')
    : prefix.concat('Button', buttonType);

  useHintGlowStyles();

  return (
    <div
      className={clsx(
        'relative flex items-center justify-center',
      )}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        boxShadow: isPressed ? '0 0 15px rgba(250, 204, 21, 0.85)' : undefined,
        transition: 'box-shadow 0.3s ease-in-out',
        animation: attractHighlight && !isPressed
          ? 'hint-circle-glow-flash 2.5s ease-in-out 3.5s infinite both'
          : undefined,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox='0 0 135 135'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`${buttonId}-gradient`} x1='129.5' y1='67.5' x2='1.5' y2='67.5' gradientUnits='userSpaceOnUse'>
            <stop offset='0.25' stopColor='#1E1E1E' stopOpacity='0.75'/>
            <stop offset='0.502358' stopColor='#666666'/>
            <stop offset='1' stopColor='white' stopOpacity='0.25'/>
          </linearGradient>
        </defs>
        <circle
          id={buttonId}
          cx='67.5' cy='67.5' r='64'
          fill={isPressed ? 'rgba(250, 204, 21, 1)' : 'white'}
          fillOpacity={isPressed ? 0.9 : 0.1}
          shapeRendering='crispEdges'
          style={attractHighlight && !isPressed
            ? { animation: 'hint-circle-bg-flash 2.5s ease-in-out 3.5s infinite both' }
            : undefined}
        />
        <circle cx='67.5' cy='67.5' r='64' stroke='#444444' strokeOpacity='0.5' strokeWidth='3' fill='none' shapeRendering='crispEdges'/>
        <circle cx='67.5' cy='67.5' r='64' stroke={`url(#${buttonId}-gradient)`} strokeOpacity='0.5' strokeWidth='3' fill='none' shapeRendering='crispEdges'/>
      </svg>

      {/* Custom icon overlay */}
      {iconUrl && (
        <img
          src={iconUrl}
          alt={label || 'button icon'}
          className='pointer-events-none absolute'
          style={{
            width: `${size * 0.6}px`,
            height: `${size * 0.6}px`,
            objectFit: 'contain',
          }}
          draggable={false}
        />
      )}

      {/* Fallback label if no icon */}
      {!iconUrl && label && (
        <span
          className='pointer-events-none absolute text-white opacity-75'
          style={{
            fontSize: `${size * 0.25}px`,
            fontWeight: 'bold',
          }}
        >
          {label}
        </span>
      )}


      {attractHighlight && !isPressed && (
        <div
          className='pointer-events-none absolute'
          style={{ top: '50%', left: '50%' }}
        >
          <div style={{ animation: 'hint-hand-enter 2s ease-out 1.5s both' }}>
            <HintHandIcon size={size * 0.50} />
          </div>
        </div>
      )}
    </div>
  );
};
